/**
 * A review item's short copy (AIC-372; db/migrations/0040_review_item_copy.sql): the analyst's action as a title, the
 * "Company · issue type" label and a one-line summary, as the approved Today / Review prototype shows them. The long
 * `reason` stays the evidence.
 *
 * This module is the field-independent half, pure and dependency-free (agent-runner and the portal-facing core use it):
 * the limits, the checks the verifier's raise_review_item input must pass, sentence clipping, and the copy of every
 * producer whose item names no field. The fallback from (kind, field_id, reason), which needs the field catalogue for
 * readable field names, is @swtracker/tracker-data/review-copy (reviewItemFallback), built on this.
 */

/** 0040's CHECKs, in characters (code points, as Postgres char_length counts). */
export const REVIEW_TITLE_MAX = 80;
export const REVIEW_SUMMARY_MAX = 160;
export const REVIEW_ISSUE_LABEL_MAX = 40;
/** "Before you decide" guidance (0040's guidance CHECK). */
export const REVIEW_GUIDANCE_MAX = 400;
/** The fallback summary: the reason's first sentence, cut on a word boundary at about this many characters. */
export const REVIEW_SUMMARY_FALLBACK_MAX = 120;

export interface ReviewItemCopy { title: string; summary: string; issue_label: string }

const chars = (s: string) => [...s].length;

/**
 * `s` at most `max` characters (code points): whole if it fits, else cut at the last whitespace before the limit plus
 * "…", never inside a word. Only a single word longer than the limit is cut inside it.
 */
export function clipWords(s: string, max: number): string {
  const t = s.replace(/\s+/g, " ").trim();
  const cps = [...t];
  if (cps.length <= max) return t;
  const head = cps.slice(0, max - 1);
  // The character after the kept part is a space: the cut already falls between words.
  let cut = cps[max - 1] === " " ? head.join("") : head.join("").replace(/\s+\S*$/u, "");
  if (!cut) cut = head.join("");
  cut = cut.replace(/[\s,;:—–-]+$/u, "");
  return `${cut || head.join("")}…`;
}

/**
 * The first sentence of `text`: up to the first ". ", "! " or "? " (a full stop after a lone capital or a known
 * abbreviation, "e.g." or "vs.", is not an end), or all of it.
 */
export function firstSentence(text: string): string {
  const t = text.replace(/\s+/g, " ").trim();
  const re = /[.!?](?=\s+\S)/g;
  for (let m = re.exec(t); m; m = re.exec(t)) {
    const before = t.slice(0, m.index);
    if (/(?:^|\s)(?:[A-Z]|e\.g|i\.e|vs|approx|etc|incl)$/i.test(before) && m[0] === ".") continue;
    return t.slice(0, m.index + 1);
  }
  return t;
}

/** The fallback summary: the reason's first sentence, cut at about REVIEW_SUMMARY_FALLBACK_MAX on a word boundary. */
export const summaryFromReason = (reason: string, max = REVIEW_SUMMARY_FALLBACK_MAX): string => clipWords(firstSentence(reason), max) || "Open the item to see the details.";

/** A field id, or any snake_case identifier ("sw_rev_total", "sw_rev_total:2026H1"): jargon the copy never carries. */
export const COPY_JARGON = /\b[a-z0-9]+_[a-z0-9_]+(?::[A-Za-z0-9_ .&/(),-]+)?\b|\b[a-z0-9_]+:(?:\d{4}(?:H[12])?)\b/;

/**
 * What is wrong with the verifier's copy (raise_review_item), [] when it is fine. The schema bounds the lengths; this
 * adds what a schema can't: words, not field ids or snake_case; a title that starts with a capital (verb first is the
 * prompt's job); a summary that is one line.
 */
export function reviewCopyProblems(c: { title: string; summary: string; issue_label?: string | undefined; guidance?: string | undefined }): string[] {
  const out: string[] = [];
  const title = c.title.trim(), summary = c.summary.trim(), label = c.issue_label?.trim(), guidance = c.guidance?.trim();
  if (!title) out.push("title is empty");
  else {
    if (chars(title) > REVIEW_TITLE_MAX) out.push(`title is longer than ${REVIEW_TITLE_MAX} characters`);
    if (!/^\p{Lu}/u.test(title)) out.push("title must start with a capitalised verb, e.g. \"Check a first-half revenue answer\"");
    if (COPY_JARGON.test(title)) out.push("title names a field id; use plain words (e.g. \"first-half revenue\")");
    if (/[\r\n]/.test(title)) out.push("title must be one line");
  }
  if (!summary) out.push("summary is empty");
  else {
    if (chars(summary) > REVIEW_SUMMARY_MAX) out.push(`summary is longer than ${REVIEW_SUMMARY_MAX} characters`);
    if (COPY_JARGON.test(summary)) out.push("summary names a field id; use plain words");
    if (/[\r\n]/.test(summary)) out.push("summary must be one sentence on one line");
  }
  if (label !== undefined) {
    if (!label) out.push("issue_label is empty");
    else if (chars(label) > REVIEW_ISSUE_LABEL_MAX) out.push(`issue_label is longer than ${REVIEW_ISSUE_LABEL_MAX} characters`);
    else if (COPY_JARGON.test(label)) out.push("issue_label names a field id; use plain words");
  }
  if (guidance !== undefined) {
    if (!guidance) out.push("guidance is empty");
    else if (chars(guidance) > REVIEW_GUIDANCE_MAX) out.push(`guidance is longer than ${REVIEW_GUIDANCE_MAX} characters`);
    else if (COPY_JARGON.test(guidance)) out.push("guidance names a field id; use plain words");
  }
  return out;
}

/** The copy trimmed and bounded to 0040's limits (for producers' own strings; the verifier's are validated, not cut). */
export function boundedCopy(c: ReviewItemCopy): ReviewItemCopy {
  return {
    title: clipWords(c.title, REVIEW_TITLE_MAX), summary: clipWords(c.summary, REVIEW_SUMMARY_MAX), issue_label: clipWords(c.issue_label, REVIEW_ISSUE_LABEL_MAX),
  };
}

/**
 * The copy of producers whose items name no field. Each is the stored copy at creation; for an older row (NULL copy)
 * reviewItemFallback recognises the same producer from its reason and serves the same copy.
 */
export const PRODUCER_COPY = {
  /** agent-runner: the model refused an agent session. */
  runner_refusal: { title: "Check an agent session the model refused", issue_label: "Agent refused",
    summary: "An agent stopped because the model refused to continue, so its work for this item is unfinished." },
  /** agent-runner: an agent session hit its budget. */
  runner_budget: { title: "Check an agent session that hit its budget", issue_label: "Budget reached",
    summary: "An agent stopped when its session reached the cost budget, so its work for this item may be unfinished." },
  /** agent-runner: a job whose lease kept lapsing (AIC-140). */
  runner_crash_loop: { title: "Check an agent job that keeps failing", issue_label: "Job failing",
    summary: "An agent job failed repeatedly and was stopped; check the runner logs before queueing it again." },
  /** end_interview escalate_to_analyst; the vendor page's "Talk to an analyst instead" (AIC-90). */
  analyst_requested: { title: "Contact the vendor who asked for an analyst", issue_label: "Analyst requested",
    summary: "The vendor asked to speak with an analyst instead of continuing the interview." },
  /** The vendor used the analyst-contact option on the thank-you page, after the session was submitted (AIC-502). */
  analyst_requested_after: { title: "Contact the vendor who asked for an analyst", issue_label: "Analyst requested",
    summary: "The vendor asked an analyst to contact them after the interview." },
  /** end_interview technical_problem. */
  technical_problem: { title: "Check an interview that ended on a technical problem", issue_label: "Technical problem",
    summary: "The voice interview ended because of a technical problem, so some answers may be missing." },
  /** The voice gateway's policy-claim item (AIC-176). */
  policy_claim: { title: "Correct a policy statement with the vendor", issue_label: "Policy statement",
    summary: "The AI interviewer said something about IDC policy that is not an approved statement." },
  /** flag_sensitive: the vendor said something is confidential. */
  confidential: { title: "Review a confidential remark", issue_label: "Confidential",
    summary: "The vendor said part of the interview is confidential; answers from that point are marked sensitive." },
  /** A vendor question the approved statements do not cover, or one that was not captured (AIC-176, AIC-270). */
  vendor_question: { title: "Answer a vendor question", issue_label: "Vendor question",
    summary: "The vendor asked a question during the interview, and an analyst needs to follow up." },
  /** Reminder not drafted: Outlook not connected (create_reminder_draft, AIC-109). */
  reminder_connect: { title: "Connect Outlook to draft a reminder", issue_label: "Reminder not drafted",
    summary: "A vendor reminder was due, but it could not be drafted because Outlook is not connected." },
  reminder_reconnect: { title: "Reconnect Outlook to draft a reminder", issue_label: "Reminder not drafted",
    summary: "A vendor reminder was due, but it could not be drafted because Outlook needs to be reconnected." },
} as const satisfies Record<string, ReviewItemCopy>;
export type ProducerCopyKey = keyof typeof PRODUCER_COPY;

/** The form's "keep this answer confidential" (AIC-233): one answer is held, not the rest of the interview. `what` is plain words ("Software revenue, 2025"). */
export function formConfidentialCopy(what: string): ReviewItemCopy {
  return boundedCopy({
    title: "Review a confidential answer", issue_label: "Confidential",
    summary: `The vendor marked ${what} confidential on the form; that answer is held for analyst-only review.`,
  });
}

/**
 * The gateway's vendor questions by topic (voice-gateway approved-statements.ts vendorQuestion: "Vendor asked about
 * pricing — analyst to follow up"): the title for each paraphrase. The publication question is the prototype's.
 */
export const VENDOR_QUESTION_TITLES: readonly (readonly [string, string])[] = [
  ["whether their figures end up in a published report", "Clarify how the response will be used"],
  ["about consent", "Answer a question about consent"],
  ["about confidentiality", "Answer a question about confidentiality"],
  ["about privacy or data protection", "Answer a question about data privacy"],
  ["about who the data is shared with", "Explain who the data is shared with"],
  ["about selling data", "Answer a question about selling data"],
  ["about who sees the data", "Explain who sees the data"],
  ["about legal terms", "Answer a question about legal terms"],
  ["about pricing", "Answer a question about pricing"],
  ["about how the recording is kept", "Explain how the recording is kept"],
  ["about anonymity or aggregation", "Explain how the figures are anonymised"],
];

/** A gateway vendor-question item's copy from its reason (the title by its first topic; the summary from the reason). */
export function vendorQuestionCopy(reason: string): ReviewItemCopy {
  const asked = /^Vendor asked (.+?)(?: — analyst to follow up)?$/.exec(reason.trim())?.[1] ?? "";
  const hit = VENDOR_QUESTION_TITLES.filter(([what]) => asked.includes(what)).sort((x, y) => asked.indexOf(x[0]) - asked.indexOf(y[0]))[0];
  if (!hit) return PRODUCER_COPY.vendor_question;
  const topics = asked.replace(/ — analyst to follow up$/, "");
  return boundedCopy({ title: hit[1], issue_label: "Vendor question", summary: `The vendor asked ${topics}, and an analyst needs to follow up.` });
}

/** The partial-interview follow-up's copy (AIC-252) from its reason ("Partial interview: 12 of 20 answered, ..."). */
export function partialFollowUpCopy(reason: string): ReviewItemCopy {
  const m = /^Partial interview: (\d+) of (\d+) answered/.exec(reason);
  return boundedCopy({
    title: "Follow up on a partial interview", issue_label: "Partial interview",
    summary: m ? `The vendor answered ${m[1]} of ${m[2]} questions before the interview stopped; they can resume on the same link.`
      : "The interview stopped before every question was answered; the vendor can resume on the same link.",
  });
}

/** The not-covered item's copy (AIC-157) from its reason ("Not covered in the voice interview: 3 questions were ..."). */
export function notCoveredCopy(reason: string): ReviewItemCopy {
  const n = Number(/: (\d+) questions? (?:was|were) not covered/.exec(reason)?.[1] ?? 0);
  return n === 1
    ? { title: "Resolve a missing interview answer", issue_label: "Missing answer", summary: "One question was not covered in the voice interview, so its answer is missing." }
    : { title: "Resolve missing interview answers", issue_label: "Missing answers",
      summary: `${n > 1 ? `${n} questions were` : "Some questions were"} not covered in the voice interview, so their answers are missing.` };
}

/**
 * The field-independent producers recognised by their reason (null: not one of them). Used by the fallback, and by
 * producers that store the copy their own reason implies.
 */
export function producerCopyFromReason(kind: string, reason: string): ReviewItemCopy | null {
  const r = reason.trim();
  if (r.startsWith("Partial interview")) return partialFollowUpCopy(r);
  if (r.startsWith("Not covered in the voice interview")) return notCoveredCopy(r);
  if (/^Outlook not connected — reminder not drafted/.test(r)) return /\bReconnect Outlook\b/.test(r) ? PRODUCER_COPY.reminder_reconnect : PRODUCER_COPY.reminder_connect;
  if (r.startsWith("The vendor asked to speak with an analyst") || r.startsWith("The vendor asked to talk to an analyst")) return PRODUCER_COPY.analyst_requested;
  if (r.startsWith("The voice interview ended on a technical problem")) return PRODUCER_COPY.technical_problem;
  if (r.startsWith("The AI interviewer said something about IDC policy")) return PRODUCER_COPY.policy_claim;
  if (/^Vendor (asked|started a side question)\b/.test(r) && /— analyst to follow up$/.test(r)) return vendorQuestionCopy(r);
  if (kind === "refusal" && /\bwas refused by the model\b/.test(r)) return PRODUCER_COPY.runner_refusal;
  if (kind === "budget" && /\bhit its \$[\d.]+ budget\b/.test(r)) return PRODUCER_COPY.runner_budget;
  if (/^[a-z-]+ job \S+ failed: its lease lapsed again/.test(r)) return PRODUCER_COPY.runner_crash_loop;
  return null;
}

// ------------------------------------------------------------------ a concern's headline (AIC-478)

/**
 * A concern's headline (AIC-478; db/migrations/0048_concern_headline.sql): the plain finding the portal shows as the title of
 * its decision dialog, e.g. "First-half 2026 looks high: $315.9M is 74% of all of 2025. It's usually 40–60%." At most
 * this many characters (code points, as 0048's CHECK counts them).
 */
export const REVIEW_HEADLINE_MAX = 120;

/** A transcript time as the verifier cites it ([mm:ss] or [h:mm:ss], brackets optional): never in a headline. */
export const HEADLINE_TIME = /\[?\b\d{1,2}:[0-5]\d(?::[0-5]\d)?\b\]?/;

/**
 * The internal kinds a concern carries (review item kinds and red-flag categories, tools/catalog.ts), in words. A headline
 * that is one of them, or leads with one as a label ("Other: ...", "Basis mismatch — ..."), says nothing to an analyst.
 */
export const HEADLINE_INTERNAL_KINDS = [
  "disagreement", "escalation", "refusal", "budget", "anomaly", "custom question", "other",
  "ai identity challenge mishandled", "confidentiality trigger missed", "stt misrecognition", "implausible figure unchallenged",
  "consent not explicit", "basis mismatch",
] as const;
const KIND_WORDS = HEADLINE_INTERNAL_KINDS.map((k) => k.replace(/ /g, "[ _]")).join("|");
/** The whole headline is a kind, or it starts with one followed by a label separator. */
const KIND_ONLY = new RegExp(`^(?:${KIND_WORDS})[.!]?$`, "i");
const KIND_LABEL = new RegExp(`^(?:${KIND_WORDS})\\s*(?::|·|—|–|\\s-\\s|\\|)\\s*`, "i");

/**
 * What is wrong with a verifier's headline, [] when it is fine. The verifier is refused (it rewrites the headline or
 * leaves it out) rather than having the text cut or stripped: a headline is one or two plain sentences, and taking a
 * tool name or a time out of the middle of one leaves a broken sentence the analyst would read as the title.
 * The refusals: empty, longer than REVIEW_HEADLINE_MAX, more than one line, a tool name, field id or any other
 * snake_case identifier (COPY_JARGON; every tool name is snake_case), a transcript time, an internal kind as the text or
 * as its label.
 */
export function headlineProblems(headline: string, label = "headline"): string[] {
  const h = headline.trim();
  if (!h) return [`${label} is empty; leave it out or write the finding in plain words`];
  const out: string[] = [];
  if (chars(h) > REVIEW_HEADLINE_MAX) out.push(`${label} is longer than ${REVIEW_HEADLINE_MAX} characters`);
  if (/[\r\n]/.test(h)) out.push(`${label} must be on one line`);
  if (COPY_JARGON.test(h)) out.push(`${label} names a tool or field id; use plain words (e.g. "first-half 2026 revenue")`);
  if (HEADLINE_TIME.test(h)) out.push(`${label} carries a transcript time; leave times for the rationale`);
  if (KIND_ONLY.test(h) || KIND_LABEL.test(h)) out.push(`${label} starts with an internal kind; say what was found instead`);
  return out;
}

/** A period code ("2026H1", "2026Q3"): a label for the survey, never a word a vendor reads. */
const PERIOD_CODE = /\b\d{4}[HQ][1-4]\b/;

/**
 * What is wrong with words a vendor will read or hear (AIC-557): a question the analyst's draft or the follow-up
 * interview puts to them, or the interview spec's opening, closing, topic titles and prompts. [] when fine. A field id,
 * tool name or any other snake_case identifier (COPY_JARGON) or a period code is refused so the author rewrites it in
 * plain words; nothing is cut out of the middle of a sentence.
 */
export function vendorCopyProblems(text: string, label: string): string[] {
  if (COPY_JARGON.test(text) || PERIOD_CODE.test(text)) {
    return [`${label} names a field id, tool name or period code; vendors read this, so say it in plain words (e.g. "software revenue for the first half of 2026")`];
  }
  return [];
}

const TIME_SRC = HEADLINE_TIME.source;
/** A run of times with an optional lead-in ("at [03:12]", "between 1:02 and 1:40", "[03:12]-[03:40]"). */
const TIME_RUN = new RegExp(`(?:\\s*\\b(?:at|around|near|by|from|to|until|after|before|between)\\b)?\\s*${TIME_SRC}(?:\\s*(?:,|and|to|–|—|-)\\s*${TIME_SRC})*`, "gi");
/** A bracketed or parenthesised aside that names an identifier: dropped whole. */
const ASIDE = /\s*[([][^()[\]]*?\b[a-z0-9]+_[a-z0-9_]+\b[^()[\]]*?[)\]]/g;
/** An aside left with nothing but a lead-in once its times are gone: "(see )", "(at -)", "()". */
const EMPTY_ASIDE = /\s*[([]\s*(?:(?:see|at|around|from|between)\b)?[\s,;:–—-]*[)\]]/gi;
/** An identifier that is the subject of what follows ("check_period_share gives 74%"). */
const SUBJECT_IDENT = /`?\b[a-z0-9]+_[a-z0-9_]+\b`?(?=\s+(?:gives|shows|returns|puts|finds|says)\b)/g;
/** A lead-in left before "the calculation" at the start of a sentence. */
const LEAD_IN_CALCULATION = /(^|[.!?]\s+)(?:via|per|using)\s+(?=the calculation\b)/gi;
/** A snake_case identifier (a tool name, or a field id the catalogue does not know), in backticks or not, with a lead-in ("via", "per", "using"). */
const IDENT = /(?:\s*\b(?:[Vv]ia|[Pp]er|[Uu]sing)\b)?(?:\s*\b(?:[Tt]ool|[Kk]ind|[Ff]ield|field_id|[Cc]heck|[Ii]d|[Tt]ype|[Cc]ategory|[Ff]lag)\s*[:=])?\s*`?\b[a-z0-9]+_[a-z0-9_]+(?::(?:North|South|East|West|Latin|Central|Middle|Asia|Rest|Eastern|Western|Northern|Southern|Southeast|Other) [A-Z][A-Za-z0-9_.&/-]*|:[A-Za-z0-9_.&/-]+)*\b`?/g;
/** A parenthesised aside (no nested parentheses; square brackets inside are fine). */
const PAREN_ASIDE = /\s*\(([^()]*)\)/g;

/**
 * Plain text for a headline from text that was not written as one (the fallback, AIC-478): transcript times with their
 * lead-ins ("at [03:12]"), asides that name an identifier, tool names and other snake_case identifiers (one that is the
 * subject of "gives" / "shows" / ... becomes "the calculation"), and a leading internal kind label are taken out, and the
 * spacing and punctuation left behind are tidied. Field ids the catalogue knows are written out first by the caller
 * (tracker-data plainFieldIds), so only unknown identifiers reach here.
 */
export function cleanHeadlineText(text: string): string {
  let t = text.replace(/\s+/g, " ").trim();
  // An aside that cited a time or named an identifier is dropped whole: what is left of it ("( prompt, vendor: ...)") is not a thought.
  t = t.replace(PAREN_ASIDE, (all, inner: string) => (new RegExp(TIME_SRC).test(inner) || /\b[a-z0-9]+_[a-z0-9_]+\b/.test(inner) ? "" : all));
  t = t.replace(TIME_RUN, "");
  t = t.replace(ASIDE, "");
  t = t.replace(EMPTY_ASIDE, "");
  t = t.replace(SUBJECT_IDENT, "the calculation");
  // "Via check_x_y gives 74%." reads "The calculation gives 74%.", not "Via the calculation gives 74%."
  t = t.replace(LEAD_IN_CALCULATION, "$1");
  t = t.replace(IDENT, "");
  t = t.replace(KIND_LABEL, "");
  // An aside emptied by the removals, and a "(" left open: only the bracket itself goes, never the text after it.
  t = t.replace(/\s*\(\s*\)/g, "").replace(/\s*\((?![^()]*\))/g, " ");
  t = t
    .replace(/\s+([,.;:!?)])/g, "$1")
    .replace(/([,;:])(?:\s*[,;:])+/g, "$1")
    .replace(/,\s*([.!?])/g, "$1")
    .replace(/([.!?])\s*\.+/g, "$1")
    .replace(/^[\s,;:.—–-]+/, "")
    .replace(/\s+/g, " ")
    .trim();
  return t ? t[0]!.toUpperCase() + t.slice(1) : t;
}
